import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Modal, Button, Form, Badge } from 'react-bootstrap';
import toastr from 'toastr';

const INTEGRATION_TYPES = [
    { value: 'instrument', label: 'Instrument', icon: 'fa-flask', description: 'Lab analyzers (Roche, Abbott, Siemens, etc.)' },
    { value: 'ehr', label: 'EHR / EMR', icon: 'fa-heartbeat', description: 'Electronic Health Record systems' },
    { value: 'lis', label: 'Other LIS', icon: 'fa-database', description: 'Laboratory Information Systems' },
    { value: 'billing', label: 'Billing System', icon: 'fa-dollar-sign', description: 'Billing / RCM platforms' },
    { value: 'device', label: 'Device / IoT', icon: 'fa-microchip', description: 'Raspberry Pi, barcode readers, printers' },
    { value: 'other', label: 'Other', icon: 'fa-plug', description: 'Custom or generic integration' }
];

const CONNECTION_METHODS = [
    { value: 'api_rest', label: 'REST API' },
    { value: 'api_soap', label: 'SOAP / WSDL' },
    { value: 'hl7_mllp', label: 'HL7 v2 (MLLP)' },
    { value: 'hl7_fhir', label: 'HL7 FHIR R4' },
    { value: 'flat_file_sftp', label: 'Flat File — SFTP' },
    { value: 'flat_file_ftp', label: 'Flat File — FTP' },
    { value: 'flat_file_local', label: 'Flat File — Local / USB' },
    { value: 'astm', label: 'ASTM (serial / TCP)' },
    { value: 'database', label: 'Direct DB connection' },
    { value: 'webhook', label: 'Webhook (inbound)' },
    { value: 'raspi', label: 'Raspberry Pi bridge' },
    { value: 'other', label: 'Other / Custom' }
];

const DATA_DIRECTIONS = [
    { value: 'inbound', label: 'Inbound (receive into LIS)', icon: 'fa-arrow-down' },
    { value: 'outbound', label: 'Outbound (send from LIS)', icon: 'fa-arrow-up' },
    { value: 'bidirectional', label: 'Bidirectional', icon: 'fa-arrows-v' }
];

const STATUS_COLORS = { active: 'success', inactive: 'secondary', error: 'danger', testing: 'warning' };

const MOCK_INTEGRATIONS = [
    { _id: '1', name: 'Roche cobas c 502', type: 'instrument', connectionMethod: 'astm', direction: 'bidirectional', status: 'active', host: '192.168.1.50', port: '2020', description: 'Chemistry analyzer — orders out, results in via ASTM serial', lastSync: '2026-10-28 09:14 AM', facility: 'Main Lab' },
    { _id: '2', name: 'Epic MyChart', type: 'ehr', connectionMethod: 'hl7_fhir', direction: 'bidirectional', status: 'active', host: 'https://epic.example.org/fhir/R4', port: '', description: 'Orders in from Epic, results out via FHIR R4', lastSync: '2026-10-28 11:02 AM', facility: 'All Facilities' },
    { _id: '3', name: 'Billing — TriZetto', type: 'billing', connectionMethod: 'flat_file_sftp', direction: 'outbound', status: 'error', host: 'sftp.trizetto.example.com', port: '22', description: 'HL7 837P billing claims via SFTP drop', lastSync: '2026-10-27 03:00 PM', facility: 'All Facilities' },
    { _id: '4', name: 'Lab Barcode Scanner Pi', type: 'device', connectionMethod: 'raspi', direction: 'inbound', status: 'testing', host: '192.168.1.99', port: '8080', description: 'Raspberry Pi 4 — USB barcode readers + label printer bridge', lastSync: 'Never', facility: 'Main Lab' }
];

const emptyForm = { name: '', type: 'instrument', connectionMethod: 'api_rest', direction: 'bidirectional', status: 'inactive', host: '', port: '', username: '', password: '', apiKey: '', description: '', facility: 'All Facilities', notes: '' };

class IntegrationsPage extends Component {
    constructor(props) {
        super(props);
        this.state = { integrations: MOCK_INTEGRATIONS, showModal: false, editingId: null, form: { ...emptyForm }, activeTab: 'all', testingId: null, testResult: null };
    }

    componentDidUpdate(prevProps) {
        if (this.props.location && prevProps.location && this.props.location.key !== prevProps.location.key) {
            this.setState({ showModal: false, editingId: null, testResult: null });
        }
    }

    openAdd = () => this.setState({ showModal: true, editingId: null, form: { ...emptyForm }, testResult: null });

    openEdit = (item) => this.setState({ showModal: true, editingId: item._id, form: { name: item.name, type: item.type, connectionMethod: item.connectionMethod, direction: item.direction, status: item.status, host: item.host || '', port: item.port || '', username: item.username || '', password: '', apiKey: item.apiKey || '', description: item.description || '', facility: item.facility || 'All Facilities', notes: item.notes || '' }, testResult: null });

    handleSave = () => {
        const { form, editingId, integrations } = this.state;
        if (!form.name.trim()) { toastr.error('Name is required'); return; }
        if (editingId) {
            this.setState({ integrations: integrations.map(i => i._id === editingId ? { ...i, ...form } : i), showModal: false });
            toastr.success('Integration updated');
        } else {
            this.setState({ integrations: [...integrations, { ...form, _id: Date.now().toString(), lastSync: 'Never' }], showModal: false });
            toastr.success('Integration added');
        }
    };

    handleDelete = (id) => {
        if (!window.confirm('Delete this integration?')) return;
        this.setState({ integrations: this.state.integrations.filter(i => i._id !== id) });
        toastr.success('Integration deleted');
    };

    handleTest = (item) => {
        this.setState({ testingId: item._id, testResult: null });
        setTimeout(() => {
            const ok = item.status !== 'error';
            this.setState({ testingId: null, testResult: { id: item._id, ok, message: ok ? 'Connection successful' : 'Connection failed: timeout after 5s' } });
        }, 2000);
    };

    setField = (field, value) => this.setState(prev => ({ form: { ...prev.form, [field]: value } }));

    filteredIntegrations = () => {
        const { integrations, activeTab } = this.state;
        return activeTab === 'all' ? integrations : integrations.filter(i => i.type === activeTab);
    };

    renderTypeBadge = (type) => {
        const t = INTEGRATION_TYPES.find(x => x.value === type);
        return t ? <span className="badge badge-light border mr-1"><i className={`fa ${t.icon} mr-1`}></i>{t.label}</span> : null;
    };

    renderMethodBadge = (method) => {
        const m = CONNECTION_METHODS.find(x => x.value === method);
        return m ? <span className="badge badge-secondary">{m.label}</span> : null;
    };

    renderDirectionIcon = (direction) => {
        const icons = { inbound: 'fa-arrow-down', outbound: 'fa-arrow-up', bidirectional: 'fa-arrows-v' };
        const labels = { inbound: 'Inbound', outbound: 'Outbound', bidirectional: 'Bidirectional' };
        return <span title={labels[direction] || direction}><i className={`fa ${icons[direction] || 'fa-arrows-v'} text-muted ml-1`}></i></span>;
    };

    render() {
        const { showModal, form, editingId, activeTab, testingId, testResult } = this.state;
        const filtered = this.filteredIntegrations();
        const borderColors = { success: '#28a745', danger: '#dc3545', warning: '#ffc107', secondary: '#6c757d' };

        return (
            <div style={{ padding: '20px', width: '100%', flex: 1 }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h4 className="mb-0"><i className="fas fa-plug mr-2 text-primary"></i>Integrations</h4>
                        <small className="text-muted">Connect instruments, EHR/EMR, billing platforms, and devices</small>
                    </div>
                    <button className="btn btn-primary" onClick={this.openAdd}><i className="fas fa-plus mr-2"></i>Add Integration</button>
                </div>

                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <a className={`nav-link ${activeTab === 'all' ? 'active' : ''}`} href="#" onClick={e => { e.preventDefault(); this.setState({ activeTab: 'all' }); }}>
                            All ({this.state.integrations.length})
                        </a>
                    </li>
                    {INTEGRATION_TYPES.map(t => {
                        const count = this.state.integrations.filter(i => i.type === t.value).length;
                        if (!count) return null;
                        return (
                            <li className="nav-item" key={t.value}>
                                <a className={`nav-link ${activeTab === t.value ? 'active' : ''}`} href="#" onClick={e => { e.preventDefault(); this.setState({ activeTab: t.value }); }}>
                                    <i className={`fa ${t.icon} mr-1`}></i>{t.label} ({count})
                                </a>
                            </li>
                        );
                    })}
                </ul>

                <div className="row">
                    {filtered.length === 0 && (
                        <div className="col-12 text-center text-muted py-5">
                            <i className="fas fa-plug fa-3x mb-3 d-block"></i>
                            No integrations yet. Click <b>Add Integration</b> to get started.
                        </div>
                    )}
                    {filtered.map(item => {
                        const tr = testResult && testResult.id === item._id ? testResult : null;
                        const borderColor = borderColors[STATUS_COLORS[item.status]] || '#6c757d';
                        return (
                            <div className="col-12 col-md-6 col-xl-4 mb-3" key={item._id}>
                                <div className="card h-100" style={{ borderLeft: `4px solid ${borderColor}` }}>
                                    <div className="card-body pb-2">
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            <h6 className="card-title mb-0 font-weight-bold">{item.name}</h6>
                                            <Badge variant={STATUS_COLORS[item.status] || 'secondary'}>{item.status}</Badge>
                                        </div>
                                        <div className="mb-2">
                                            {this.renderTypeBadge(item.type)}
                                            {this.renderMethodBadge(item.connectionMethod)}
                                            {this.renderDirectionIcon(item.direction)}
                                        </div>
                                        {item.description && <p className="text-muted small mb-1">{item.description}</p>}
                                        <div className="small text-muted">
                                            {item.host && <span><i className="fa fa-server mr-1"></i>{item.host}{item.port ? `:${item.port}` : ''}</span>}
                                        </div>
                                        <div className="small text-muted mt-1">
                                            <i className="fa fa-clock-o mr-1"></i>Last sync: {item.lastSync} &nbsp;·&nbsp; <i className="fa fa-building mr-1"></i>{item.facility}
                                        </div>
                                        {tr && (
                                            <div className={`alert alert-${tr.ok ? 'success' : 'danger'} py-1 px-2 mt-2 mb-0 small`}>
                                                <i className={`fa fa-${tr.ok ? 'check' : 'times'} mr-1`}></i>{tr.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-footer bg-transparent pt-1 pb-2" style={{ display: 'flex', gap: '6px' }}>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => this.openEdit(item)}><i className="fas fa-pen mr-1"></i>Edit</button>
                                        <button className="btn btn-sm btn-outline-primary" disabled={testingId === item._id} onClick={() => this.handleTest(item)}>
                                            {testingId === item._id ? <><i className="fa fa-spinner fa-spin mr-1"></i>Testing...</> : <><i className="fa fa-plug mr-1"></i>Test</>}
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger ml-auto" onClick={() => this.handleDelete(item._id)}><i className="fa fa-trash"></i></button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <Modal show={showModal} onHide={() => this.setState({ showModal: false })} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title><i className="fas fa-plug mr-2"></i>{editingId ? 'Edit Integration' : 'Add Integration'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row">
                            <div className="col-12 col-md-8">
                                <Form.Group><Form.Label>Integration Name *</Form.Label><Form.Control value={form.name} onChange={e => this.setField('name', e.target.value)} placeholder="e.g. Roche cobas c 502, Epic MyChart" /></Form.Group>
                            </div>
                            <div className="col-12 col-md-4">
                                <Form.Group><Form.Label>Status</Form.Label>
                                    <Form.Control as="select" value={form.status} onChange={e => this.setField('status', e.target.value)}>
                                        <option value="inactive">Inactive</option><option value="testing">Testing</option><option value="active">Active</option><option value="error">Error</option>
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-12 col-md-4">
                                <Form.Group><Form.Label>Integration Type</Form.Label>
                                    <Form.Control as="select" value={form.type} onChange={e => this.setField('type', e.target.value)}>
                                        {INTEGRATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} — {t.description}</option>)}
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-12 col-md-4">
                                <Form.Group><Form.Label>Connection Method</Form.Label>
                                    <Form.Control as="select" value={form.connectionMethod} onChange={e => this.setField('connectionMethod', e.target.value)}>
                                        {CONNECTION_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-12 col-md-4">
                                <Form.Group><Form.Label>Data Direction</Form.Label>
                                    <Form.Control as="select" value={form.direction} onChange={e => this.setField('direction', e.target.value)}>
                                        {DATA_DIRECTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-12 col-md-8">
                                <Form.Group><Form.Label>Host / URL / IP</Form.Label><Form.Control value={form.host} onChange={e => this.setField('host', e.target.value)} placeholder="192.168.1.50 or https://api.example.com/fhir/R4" /></Form.Group>
                            </div>
                            <div className="col-12 col-md-4">
                                <Form.Group><Form.Label>Port</Form.Label><Form.Control value={form.port} onChange={e => this.setField('port', e.target.value)} placeholder="e.g. 2020, 22, 8080" /></Form.Group>
                            </div>
                            <div className="col-12 col-md-6">
                                <Form.Group><Form.Label>Username / Client ID</Form.Label><Form.Control value={form.username} onChange={e => this.setField('username', e.target.value)} placeholder="Username or client ID" /></Form.Group>
                            </div>
                            <div className="col-12 col-md-6">
                                <Form.Group><Form.Label>Password / Secret</Form.Label><Form.Control type="password" value={form.password} onChange={e => this.setField('password', e.target.value)} placeholder={editingId ? '(unchanged)' : 'Password or client secret'} /></Form.Group>
                            </div>
                            <div className="col-12">
                                <Form.Group><Form.Label>API Key / Token</Form.Label><Form.Control value={form.apiKey} onChange={e => this.setField('apiKey', e.target.value)} placeholder="Bearer token, API key, or SFTP private key path" /></Form.Group>
                            </div>
                            <div className="col-12 col-md-6">
                                <Form.Group><Form.Label>Facility</Form.Label><Form.Control value={form.facility} onChange={e => this.setField('facility', e.target.value)} placeholder="All Facilities" /></Form.Group>
                            </div>
                            <div className="col-12">
                                <Form.Group><Form.Label>Description</Form.Label><Form.Control value={form.description} onChange={e => this.setField('description', e.target.value)} placeholder="Short description of what this integration does" /></Form.Group>
                            </div>
                            <div className="col-12">
                                <Form.Group><Form.Label>Notes / Setup Instructions</Form.Label><Form.Control as="textarea" rows={3} value={form.notes} onChange={e => this.setField('notes', e.target.value)} placeholder="Any additional notes, setup steps, or references" /></Form.Group>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showModal: false })}>Cancel</Button>
                        <Button variant="primary" onClick={this.handleSave}><i className="fa fa-save mr-1"></i>{editingId ? 'Save Changes' : 'Add Integration'}</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}
export default withRouter(IntegrationsPage);
